import { useQuery } from '@tanstack/react-query';

import {
  TraitementResponseDto,
  TraitementResponseDtoListStatutEnum,
} from 'lib_api/lib/api/gen';

import { useApi } from '../ApiStoreContext';

type VerificationFunction = (motifId: string) => boolean;

interface VerificationLogicReturn {
  isVerificationProprietaireRequired: VerificationFunction;
  isVerificationCodeMLRequired: VerificationFunction;
}

export function useVerificationLogic(
  traitement?: TraitementResponseDto | null,
): VerificationLogicReturn {
  const { DossierControllerApi } = useApi();

  // 1. Fetch the list of IDs from the API
  const { data: requiredVerifInfosProprietaireMotifIds = [] } = useQuery({
    queryKey: ['motifs-requiring-verification-infos-proprietaire'],
    queryFn: async () => {
      const result =
        await DossierControllerApi.getMotifsSortieRequiringProprietaireInfoCheckUsingGET();
      return result.idsCorrelation ?? [];
    },
    // Keep data fresh/cached as this likely doesn't change often
    staleTime: Infinity,
  });

  // 2. Logic Check
  const hasStatusVendu = traitement?.listStatut?.includes(
    TraitementResponseDtoListStatutEnum.VENDU,
  );

  // 3. Return a clean predicate function
  const isVerificationProprietaireRequired = (motifId: string): boolean => {
    if (!hasStatusVendu) {
      return false;
    }
    return requiredVerifInfosProprietaireMotifIds.includes(motifId);
  };

  // 1. Fetch the list of IDs from the API
  const { data: requiredVerifCodeMLMotifIds = [] } = useQuery({
    queryKey: ['motifs-requiring-verification-code-ml'],
    queryFn: async () => {
      const result =
        await DossierControllerApi.getMotifsSortieRequiringCodeMLCheckUsingGET();
      return result.idsCorrelation ?? [];
    },
    // Keep data fresh/cached as this likely doesn't change often
    staleTime: Infinity,
  });

  // 2. Logic Check
  const hasCodeML = !!traitement?.mainLeveeUsager?.codeVerification;

  // 3. Return a clean predicate function
  const isVerificationCodeMLRequired = (motifId: string): boolean => {
    return requiredVerifCodeMLMotifIds.includes(motifId) && hasCodeML;
  };

  return { isVerificationProprietaireRequired, isVerificationCodeMLRequired };
}
